import * as d3 from "d3";

export type LegendItem = { color: string; label: string };

export type LegendEntryLayout = LegendItem & {
  x: number;
  y: number;
  lines: string[];
};

export type LegendLayout = {
  entries: LegendEntryLayout[];
  height: number;
  columnWidth: number;
};

export const LEGEND_COLUMNS = 3;
export const LEGEND_MIN_COLUMN_WIDTH = 220;
export const LEGEND_SWATCH_SIZE = 12;
export const LEGEND_SWATCH_GAP = 8;
export const LEGEND_COLUMN_GAP = 24;
export const LEGEND_LINE_HEIGHT = 16;
export const LEGEND_ROW_GAP = 8;
export const LEGEND_FONT_SIZE = "14px";
export const LEGEND_FONT_FAMILY = "sans-serif";
export const LEGEND_FONT = `${LEGEND_FONT_SIZE} ${LEGEND_FONT_FAMILY}`;
export const LEGEND_ELLIPSIS = "…";

// Shared chart chrome, so the charts drawing this legend cannot drift apart.
export const CHART_MIN_WIDTH = 640;
export const CHART_AXIS_LABEL_OFFSET = 46;
export const CHART_LEGEND_TOP_GAP = 28;
// Breathing room below the legend, which is the last thing every chart draws.
export const CHART_BOTTOM_PAD = 24;

// schemeCategory10 alone repeats after 10 series, which makes the legend
// ambiguous on registers with many measures. These extensions stay saturated
// enough to read against a white background.
export const CHART_PALETTE: string[] = [
  ...d3.schemeCategory10,
  ...d3.schemeDark2,
  ...d3.schemeTableau10,
];

const MAX_LINES = 2;
const FALLBACK_CHAR_WIDTH = 7.2;

let measureContext: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext === undefined) {
    try {
      measureContext = document.createElement("canvas").getContext("2d");
      if (measureContext) measureContext.font = LEGEND_FONT;
    } catch {
      measureContext = null;
    }
  }
  return measureContext;
}

/**
 * Width of `text` in pixels at the legend font. Falls back to a per-character
 * estimate in environments without a canvas 2d context (e.g. jsdom).
 */
export function measureTextWidth(text: string): number {
  const context = getMeasureContext();
  const measured =
    context && typeof context.measureText === "function"
      ? context.measureText(text).width
      : 0;
  return measured > 0 ? measured : text.length * FALLBACK_CHAR_WIDTH;
}

function ellipsize(text: string, maxWidth: number): string {
  if (measureTextWidth(text) <= maxWidth) return text;
  let truncated = text;
  while (
    truncated.length > 1 &&
    measureTextWidth(`${truncated}${LEGEND_ELLIPSIS}`) > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}${LEGEND_ELLIPSIS}`;
}

/**
 * Greedily wraps `label` to at most MAX_LINES lines that fit `maxWidth`.
 * Overflow beyond the final line is ellipsised on measured width, not on a
 * character count, so proportional fonts are handled correctly.
 */
export function wrapLabel(label: string, maxWidth: number): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];

  const lines: string[] = [];
  let current = "";

  for (let index = 0; index < words.length; index++) {
    const word = words[index];
    const candidate = current ? `${current} ${word}` : word;

    if (!current || measureTextWidth(candidate) <= maxWidth) {
      current = candidate;
      continue;
    }

    if (lines.length === MAX_LINES - 1) {
      // Final line already in progress: absorb the remainder and ellipsise it.
      current = `${current} ${words.slice(index).join(" ")}`;
      break;
    }

    // A single word can be wider than the column on its own, so every line is
    // ellipsised rather than only the last one.
    lines.push(ellipsize(current, maxWidth));
    current = word;
  }

  lines.push(ellipsize(current, maxWidth));
  return lines;
}

/**
 * Number of legend columns that fit `availableWidth`, capped at LEGEND_COLUMNS
 * so wide screens keep the established three-column look while narrow ones
 * degrade to two or one instead of squeezing labels into unreadable columns.
 */
export function resolveLegendColumns(availableWidth: number): number {
  const fitting = Math.floor(availableWidth / LEGEND_MIN_COLUMN_WIDTH);
  return Math.max(1, Math.min(LEGEND_COLUMNS, fitting));
}

/**
 * Lays legend entries out in a fixed number of columns across `availableWidth`,
 * filling row-major. Row heights adapt to the tallest wrapped label in the row.
 */
export function layoutLegend(
  items: LegendItem[],
  availableWidth: number,
  columns: number = resolveLegendColumns(availableWidth)
): LegendLayout {
  if (items.length === 0) {
    return { entries: [], height: 0, columnWidth: availableWidth };
  }

  const columnCount = Math.max(1, Math.min(columns, items.length));
  const columnWidth =
    (availableWidth - LEGEND_COLUMN_GAP * (columnCount - 1)) / columnCount;
  const textWidth = Math.max(
    40,
    columnWidth - LEGEND_SWATCH_SIZE - LEGEND_SWATCH_GAP
  );

  const wrapped = items.map((item) => ({
    ...item,
    lines: wrapLabel(item.label, textWidth),
  }));

  const rowCount = Math.ceil(wrapped.length / columnCount);
  const rowTops: number[] = [];
  let cursor = 0;

  for (let row = 0; row < rowCount; row++) {
    rowTops.push(cursor);
    const rowItems = wrapped.slice(row * columnCount, (row + 1) * columnCount);
    const tallest = Math.max(...rowItems.map((item) => item.lines.length));
    cursor += tallest * LEGEND_LINE_HEIGHT + LEGEND_ROW_GAP;
  }

  const entries = wrapped.map((item, index) => {
    const row = Math.floor(index / columnCount);
    const column = index % columnCount;
    return {
      ...item,
      x: column * (columnWidth + LEGEND_COLUMN_GAP),
      y: rowTops[row],
    };
  });

  return {
    entries,
    height: Math.max(0, cursor - LEGEND_ROW_GAP),
    columnWidth,
  };
}

/**
 * Draws a laid-out legend into an SVG container. Both the on-screen chart and
 * its export use this single path, so the two can never drift apart.
 */
export function renderLegendInto(
  parent: d3.Selection<any, unknown, any, any>,
  layout: LegendLayout,
  x: number,
  y: number
): void {
  const group = parent
    .append("g")
    .attr("class", "chart-legend")
    .attr("transform", `translate(${x}, ${y})`);

  layout.entries.forEach((entry) => {
    const item = group
      .append("g")
      .attr("class", "chart-legend-item")
      .attr("transform", `translate(${entry.x}, ${entry.y})`);

    item
      .append("rect")
      .attr("class", "chart-legend-swatch")
      .attr("width", LEGEND_SWATCH_SIZE)
      .attr("height", LEGEND_SWATCH_SIZE)
      .attr("rx", LEGEND_SWATCH_SIZE / 2)
      .attr("y", (LEGEND_LINE_HEIGHT - LEGEND_SWATCH_SIZE) / 2)
      .attr("fill", entry.color);

    const text = item
      .append("text")
      .style("font-size", LEGEND_FONT_SIZE)
      .style("font-family", LEGEND_FONT_FAMILY)
      .style("fill", "#425466");

    // Only surface a hover tooltip where the label genuinely had to be cut.
    if (entry.lines.some((line) => line.endsWith(LEGEND_ELLIPSIS))) {
      text.append("title").text(entry.label);
    }

    entry.lines.forEach((line, lineIndex) => {
      text
        .append("tspan")
        .attr("x", LEGEND_SWATCH_SIZE + LEGEND_SWATCH_GAP)
        .attr("y", lineIndex * LEGEND_LINE_HEIGHT + LEGEND_LINE_HEIGHT - 4)
        .text(line);
    });
  });
}
